import { useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { AlertTriangle, CreditCard, Landmark, Loader2, Plus } from "lucide-react";
import { API_BASE } from "../../shared/Constants";
import { PaymentContext } from "../../contexts/PaymentContext";
import { UserCard } from "../../shared/Interfaces";
import { getCardBrandIcon } from "../../shared/Utils";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";
import { reportHandledError, reportApiError } from "../../utils/reportError";

export default function PaymentMethod() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const {
    setPaymentStage,
    setCard,
    paymentOption,
    setPaymentToast,
    setPaymentMethod,
    autoChargePaymentId,
    setAutoChargePaymentId,
    billingScope,
    activeOrganizationId,
  } = useContext(PaymentContext);
  const [cards, setCards] = useState<UserCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<UserCard | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAutoChargeWarning, setShowAutoChargeWarning] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);

  const handleSelectedCard = (card: UserCard) => {
    if (selectedCard?.payment_id === card.payment_id) setSelectedCard(null);
    else setSelectedCard(card);
  };

  const buildBillingUrl = (path: string, params: Record<string, string> = {}) => {
    const search = new URLSearchParams({
      ...(billingScope === "organization" && activeOrganizationId
        ? { organization_id: activeOrganizationId }
        : {}),
      ...params,
    });
    const query = search.toString();
    return `${API_BASE}/api/billing${path}${query ? `?${query}` : ""}`;
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (billingScope === "organization" && !activeOrganizationId) {
        setCards([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const url = buildBillingUrl("/payment-methods");
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Auth ${access_token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setCards(data["customer_cards"]);
        } else {
          reportApiError(response, { feature: "payment.method.list", method: "GET" });
          setPaymentToast({
            type: "error",
            message: "Erro ao recuperar métodos de pagamento",
          });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "payment.method.list" });
        setPaymentToast({
          type: "error",
          message: "Erro ao recuperar métodos de pagamento",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingScope, activeOrganizationId, getAccessTokenSilently, setPaymentToast]);

  const HandleCardButton = async () => {
    if (selectedCard) {
      setCard(selectedCard);
      setPaymentStage("payment-saved-card");
    } else if (paymentOption === "single") {
      setPaymentMethod("card");
      setPaymentStage("payment");
    } else {
      setPaymentToast({
        type: "error",
        message: "Selecione ou cadastre um cartão de crédito para prosseguir",
      });
    }
  };

  const HandlePixButton = () => {
    setPaymentMethod("pix");
    setPaymentStage("payment-pix");
  };

  const HandleBoletoButton = () => {
    setPaymentMethod("boleto");
    setPaymentStage("payment");
  };

  const handleDeleteCardClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    payment_id: string
  ) => {
    event.stopPropagation();
    setCardToDelete(payment_id);
    setShowDeleteModal(true);
  };

  const confirmDeleteCard = async () => {
    if (!cardToDelete) return;
    setIsDeletingCard(true);
    try {
      const url = buildBillingUrl("/delete-payment-method", { payment_id: cardToDelete });
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCards(cards.filter((card) => card.payment_id !== cardToDelete));
        if (selectedCard?.payment_id === cardToDelete) setSelectedCard(null);
        if (data?.auto_charge_removed) {
          setAutoChargePaymentId("");
          setShowAutoChargeWarning(true);
          setPaymentToast({
            type: "info",
            message:
              "A recarga automática foi desativada porque o cartão selecionado foi removido.",
          });
        } else {
          setPaymentToast({ type: "success", message: "Cartão removido com sucesso" });
        }
      } else {
        reportApiError(response, { feature: "payment.method.delete", method: "POST" });
        setPaymentToast({ type: "error", message: "Erro ao remover cartão" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "payment.method.delete" });
      setPaymentToast({ type: "error", message: "Erro ao remover cartão" });
    } finally {
      setIsDeletingCard(false);
      setShowDeleteModal(false);
      setCardToDelete(null);
    }
  };

  const cancelDeleteCard = () => {
    if (isDeletingCard) return;
    setShowDeleteModal(false);
    setCardToDelete(null);
  };

  useEffect(() => {
    if (autoChargePaymentId) setShowAutoChargeWarning(false);
  }, [autoChargePaymentId]);

  let description: string;
  switch (paymentOption) {
    case "single":
      description =
        "Caso deseje pagar com um cartão de crédito, cadastre um cartão para compras futuras ou prossiga sem salvar.";
      break;
    case "automatic":
      description = "Selecione um cartão para configurar a recarga automática.";
      break;
    default:
      description = "Adicione ou remova novos cartões para futuras recargas.";
  }

  return (
    <>
      <div className="max-h-[500px] overflow-y-auto px-6 pt-5 pb-2">
        {showAutoChargeWarning && (
          <div className="mb-5 flex items-start gap-3 rounded-md border border-warning/30 bg-warning/10 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-[13px] text-foreground/90">
              A recarga automática foi desativada porque o cartão configurado foi removido.
              Configure uma nova recarga automática para continuar usando este recurso.
            </p>
          </div>
        )}

        <p className="mb-4 text-[13px] text-muted-foreground">{description}</p>

        <div className="mb-4 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-[14px] font-semibold text-foreground">Cartões cadastrados</h3>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setPaymentStage("add-card")}
            className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-4 transition-colors hover:border-foreground/30 hover:bg-muted"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Plus className="h-5 w-5" />
            </span>
            <span className="text-[13px] font-semibold text-foreground">Adicionar cartão</span>
          </button>

          {!isLoading
            ? cards.map((card) => {
                const isSelected = selectedCard?.payment_id === card.payment_id;
                return (
                  <button
                    type="button"
                    key={card.payment_id}
                    onClick={() => handleSelectedCard(card)}
                    className={cn(
                      "relative flex min-h-[140px] flex-col gap-3 rounded-lg border bg-card p-4 text-left transition-all hover:border-foreground/20",
                      isSelected ? "border-primary ring-1 ring-primary" : "border-border"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <img
                        src={getCardBrandIcon(card.card_brand)}
                        alt="Card"
                        className="h-7 w-9 rounded border border-border bg-muted p-0.5"
                      />
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleDeleteCardClick(e as any, card.payment_id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDeleteCardClick(e as any, card.payment_id); }}
                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-destructive/30 bg-destructive/10 text-[13px] font-semibold text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
                        aria-label="Remover cartão"
                      >
                        ×
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col justify-center gap-2">
                      <p className="font-mono text-[14px] font-semibold tracking-wider text-foreground">
                        •••• •••• •••• {card.card_last4}
                      </p>
                      <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                        <span>Exp: {card.card_expiration}</span>
                        <span className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                          {card.billing_name}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })
            : Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="flex min-h-[140px] flex-col gap-3 rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-7 w-9 rounded" />
                    <Skeleton className="h-5 w-5 rounded-full" />
                  </div>
                  <div className="flex flex-1 flex-col justify-center gap-2">
                    <Skeleton className="h-4 w-full" />
                    <div className="flex justify-between">
                      <Skeleton className="h-3 w-14" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {paymentOption !== "cards-only" &&
        (paymentOption === "single" ? (
          <>
            <div className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p className="text-[13px] text-foreground/90">
                <strong className="font-semibold text-foreground">Atenção:</strong> pagamentos com
                boleto bancário podem levar até 2 dias úteis para serem confirmados. Pagamentos via
                PIX são confirmados instantaneamente.
              </p>
            </div>
            <div className="flex flex-col gap-2 border-t border-border bg-muted/30 px-6 py-4 sm:flex-row">
              <Button onClick={HandlePixButton} className="flex-1 gap-2" variant="outline">
                <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-primary-foreground">
                  PIX
                </span>
                Pagar com PIX
              </Button>
              <Button onClick={HandleCardButton} className="flex-1 gap-2">
                <CreditCard className="h-4 w-4" />
                Pagar com cartão
              </Button>
              <Button onClick={HandleBoletoButton} className="flex-1 gap-2" variant="outline">
                <Landmark className="h-4 w-4" />
                Pagar com boleto
              </Button>
            </div>
          </>
        ) : (
          <div className="border-t border-border bg-muted/30 px-6 py-4">
            <Button onClick={HandleCardButton} className="w-full">
              Prosseguir
            </Button>
          </div>
        ))}

      <Dialog open={showDeleteModal} onOpenChange={(o) => !o && cancelDeleteCard()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover cartão</DialogTitle>
            <DialogDescription>
              {cardToDelete && autoChargePaymentId === cardToDelete
                ? "Este cartão está configurado para recarga automática. Ao removê-lo, a recarga automática será desativada e você precisará configurá-la novamente."
                : "Você tem certeza que quer remover este cartão?"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-md border border-warning/30 bg-warning/10 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-[13px] font-medium text-foreground">
              Esta ação não pode ser desfeita.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={cancelDeleteCard}
              disabled={isDeletingCard}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteCard}
              disabled={isDeletingCard}
              className="flex-1 gap-2"
            >
              {isDeletingCard && <Loader2 className="h-4 w-4 animate-spin" />}
              {isDeletingCard ? "Removendo..." : "Remover cartão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
